import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type Category = 'all' | 'products' | 'practice' | 'details';

interface GalleryImage {
  src: string;
  alt: string;
  caption: string;
  category: Category;
  span: 'normal' | 'wide' | 'tall';
}

export default function Gallery() {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const galleryRef = useRef<HTMLDivElement>(null);

  const categories: { key: Category; label: string }[] = [
    { key: 'all', label: t('gallery.filterAll') },
    { key: 'products', label: t('gallery.filterProducts') },
    { key: 'practice', label: t('gallery.filterPractice') },
    { key: 'details', label: t('gallery.filterDetails') },
  ];

  const images = [
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/e0e0e0e0e0e0e0e0e0e0e0e0e0e0e0e0.png',
      alt: 'MYSA Pilates Towel on Reformer',
      caption: t('gallery.caption1'),
      span: 1
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/79106ebee015947185c3c150ebffe86d.png',
      alt: 'MYSA Pilates Towel Detail',
      caption: t('gallery.caption2'),
      span: 1
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3c3.png',
      alt: 'MYSA Pilates Towel Colors',
      caption: t('gallery.caption3'),
      span: 1
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/a3e870c87526cd46965634f87778cd1b.png',
      alt: 'MYSA Pilates Towel in Use',
      caption: t('gallery.caption4'),
      span: 1
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/d9a17f2e529e765f6a9ae2593beeee6d.png',
      alt: 'MYSA Pilates Towel Texture',
      caption: t('gallery.caption5'),
      span: 2
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/f6f6f6f6f6f6f6f6f6f6f6f6f6f6f6f6.png',
      alt: 'MYSA Pilates Towel Lifestyle',
      caption: t('gallery.caption6'),
      span: 1
    }
  ];

  const galleryImages: GalleryImage[] = [
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/5fe58cd51d459dce50990540cb468836.jpeg',
      alt: t('gallery.image1Alt'),
      caption: t('gallery.image1Caption'),
      category: 'products',
      span: 'wide',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/79106ebee015947185c3c150ebffe86d.png',
      alt: t('gallery.image2Alt'),
      caption: t('gallery.image2Caption'),
      category: 'details',
      span: 'tall',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/091e46628b3d935592b60713ffe9431b.png',
      alt: t('gallery.image3Alt'),
      caption: t('gallery.image3Caption'),
      category: 'practice',
      span: 'normal',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/a3e870c87526cd46965634f87778cd1b.png',
      alt: t('gallery.image4Alt'),
      caption: t('gallery.image4Caption'),
      category: 'products',
      span: 'normal',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/55ea732f33bbb54b305ffe71fbd72be3.png',
      alt: t('gallery.image6Alt'),
      caption: t('gallery.image6Caption'),
      category: 'products',
      span: 'tall',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/97947aea76efa424b69ecce53c319648.png',
      alt: t('gallery.image7Alt'),
      caption: t('gallery.image7Caption'),
      category: 'details',
      span: 'normal',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/7d85f9f90a73edaf1a478599ed4aa3e5.png',
      alt: t('gallery.image8Alt'),
      caption: t('gallery.image8Caption'),
      category: 'practice',
      span: 'wide',
    },
    {
      src: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/f35e94876faf08ed368183041730d356.png',
      alt: t('gallery.image9Alt'),
      caption: t('gallery.image9Caption'),
      category: 'practice',
      span: 'normal',
    },
  ];

  const filteredImages = activeCategory === 'all'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.15 }
    );

    const items = galleryRef.current?.querySelectorAll('[data-index]');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [filteredImages]);

  useEffect(() => {
    setVisibleItems(new Set());
  }, [activeCategory]);

  useEffect(() => {
    if (selectedImage !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedImage]);

  const getSpanClasses = (span: string) => {
    switch (span) {
      case 'wide':
        return 'md:col-span-2 row-span-1';
      case 'tall':
        return 'col-span-1 md:row-span-2';
      default:
        return 'col-span-1 row-span-1';
    }
  };

  const getHeightClass = (span: string) => {
    switch (span) {
      case 'wide':
        return 'h-64 md:h-80';
      case 'tall':
        return 'h-64 md:h-full';
      default:
        return 'h-56 md:h-64';
    }
  };

  const getObjectPosition = (index: number) => {
    if (index === 0) return 'object-bottom';
    if (index === 4) return 'object-[75%]';
    if (index === 6) return 'object-[75%]';
    if (index === 7) return 'object-[65%]';
    return 'object-top';
  };

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-red-50 text-red-700 rounded-full text-xs font-medium tracking-wide uppercase mb-4">
            {t('gallery.label')}
          </span>
          <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            <a href="#gallery" className="hover:text-red-700 transition-colors">
              {t('gallery.title')}
            </a>
          </h4>
          <p className="text-sm text-gray-500 max-w-xl mx-auto leading-relaxed">
            {t('gallery.subtitle')}
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-stone-100 rounded-full px-1 py-1 gap-1">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div
          ref={galleryRef}
          className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 auto-rows-auto"
        >
          {filteredImages.map((image, index) => (
            <div
              key={`${activeCategory}-${index}`}
              data-index={index}
              className={`${getSpanClasses(image.span)} group cursor-pointer overflow-hidden rounded-xl relative transition-all duration-500 ${
                visibleItems.has(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 80}ms` }}
              onClick={() => setSelectedImage(index)}
            >
              <div className={`w-full ${getHeightClass(image.span)} relative`}>
                <img
                  src={image.src}
                  alt={image.alt}
                  className={`w-full h-full object-cover ${getObjectPosition(index)} transition-transform duration-700 group-hover:scale-110`}
                  loading="lazy"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                {/* Caption on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
                  <p className="text-white text-xs font-medium leading-relaxed">
                    {image.caption}
                  </p>
                </div>
                {/* Zoom icon */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                  <i className="ri-zoom-in-line text-sm text-gray-800"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Instagram CTA */}
        <div className="mt-14 text-center">
          <a
            href="https://www.instagram.com/mysacoze?igsh=YTFiYmN0cjRmdXM="
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-full text-xs font-medium hover:shadow-lg hover:shadow-red-200 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-instagram-line text-base"></i>
            {t('gallery.followInstagram')}
            <i className="ri-arrow-right-line text-sm"></i>
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          {/* Close */}
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-5 right-5 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-xl text-white"></i>
          </button>

          {/* Prev */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(
                selectedImage > 0 ? selectedImage - 1 : filteredImages.length - 1
              );
            }}
            className="absolute left-4 md:left-8 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl text-white"></i>
          </button>

          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(
                selectedImage < filteredImages.length - 1 ? selectedImage + 1 : 0
              );
            }}
            className="absolute right-4 md:right-8 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl text-white"></i>
          </button>

          {/* Image */}
          <div
            className="max-w-4xl w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filteredImages[selectedImage].src}
              alt={filteredImages[selectedImage].alt}
              className="w-full max-h-[78vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <p className="text-white/90 text-sm font-medium">
                {filteredImages[selectedImage].caption}
              </p>
              <p className="text-white/40 text-xs mt-1">
                {selectedImage + 1} / {filteredImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
